// Marco de TODAS las pantallas del módulo Diagnósticos: selector «Empresa» +
// banner «Trabajando en…» (ADR-007). Las pantallas se vuelven a montar al cambiar
// de empresa (key = org elegida), así nunca muestran datos de la empresa anterior.
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';

import { ActingOrgBar } from '@/components/acting-org/ActingOrgBar';
import { clearActingOrg, useActingOrg } from '@/lib/actingOrg';
import { orgIdFromToken } from '@/lib/api_superadmin';
import { useCompanyName } from '@/modules/orion-shell/data';
import { DIAG_SCOPE } from '../api';

export function useMyOrgName(): string {
  const company = useCompanyName();
  return company.data || orgIdFromToken() || 'Mi organización';
}

/**
 * `ownOrgOnly`: pantallas que NO se delegan (Programas y Embudo — el backend responde 403
 * con X-Acting-Org). Con una empresa elegida se explica y se ofrece volver a mi org.
 */
export function DiagFrame({ children, ownOrgOnly = false }: { children: ReactNode; ownOrgOnly?: boolean }) {
  const navigate = useNavigate();
  const { acting } = useActingOrg();
  const myOrgName = useMyOrgName();
  if (ownOrgOnly && acting) {
    return (
      <div className="min-w-0">
        <ActingOrgBar scope={DIAG_SCOPE} myOrgName={myOrgName} onManage={() => navigate('/m/diagnosticos/empresas')} />
        <div className="flex flex-col items-center gap-2 rounded-[20px] border border-dashed border-border px-4 py-10 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary"><Layers size={20} aria-hidden /></span>
          <p className="text-sm font-medium text-foreground">Esta sección es de tu organización</p>
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
            Los programas y su embudo se administran en {myOrgName}, no dentro de {acting.nombre}. Vuelve a tu
            organización para verlos, o sigue con los proyectos de {acting.nombre}.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => navigate('/m/diagnosticos/proyectos')} className="inline-flex min-h-[40px] items-center justify-center rounded-xl border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground hover:bg-muted">
              Ver proyectos de {acting.nombre}
            </button>
            <button type="button" onClick={() => clearActingOrg()} className="orion-btn-brand inline-flex min-h-[40px] items-center justify-center rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">
              Volver a mi org
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <ActingOrgBar scope={DIAG_SCOPE} myOrgName={myOrgName} onManage={() => navigate('/m/diagnosticos/empresas')} />
      <div key={acting?.org_id ?? 'mi-org'} className="min-w-0">
        {children}
      </div>
    </div>
  );
}
